import { PanelRightClose, Sparkles } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { NoteAssistantTarget } from "@/common/types/noteAssistant";
import { PracticeTour } from "@/components/ui/guided/PracticeTour";
import { V3IconButton } from "@/components/ui/v3/button";
import { V3Chip } from "@/components/ui/v3/chip";
import { V3Drawer } from "@/components/ui/v3/drawer";
import type { AssistantRequest } from "@/components/ui/visits/NoteAssistantPanel";
import { PreChartTemplateChip } from "@/components/ui/visits/PreChartTemplateChip";
import { StartVisitButton } from "@/components/ui/visits/StartVisitButton";
import { documentTemplateName } from "@/lib/document-name";
import { hasMainRecording, preChartHeadings } from "@/lib/pre-chart";
import {
  labsDefaultHeading,
  readAccountLabs,
  reportLabTests,
  settingPicks,
} from "@/lib/pre-chart-labs";
import { cn } from "@/lib/utils";
import type { VisitRecording } from "@/lib/visit-content-queries";
import { formatVisitDuration } from "@/pages/Visits/redesign/visit-list-model";
import type { FileData } from "@/services/files";
import { useAuthStore } from "@/store/auth.store";
import { usePreChartStore } from "@/store/preChart.store";
import { useSourceNames } from "@/store/sourceNames.store";
import { useMediaQuery } from "../layout";
import {
  AssistantSources,
  AttachSourceButton,
  SourceChips,
  type Props as SourcesProps,
} from "../sources/AssistantSources";
import type { AssistantUnavailableReason } from "../WorkspaceAssistant";
import { AskBar, type AskSuggestion } from "./AskBar";
import { AssistantConversation } from "./AssistantConversation";
import {
  type CopyToChart,
  NOTE_SCROLLER_ID,
  NoteActions,
  NoteMetaBar,
} from "./NoteMetaBar";
import { type PreChartActions, PreChartBanner } from "./PreChartBanner";
import {
  PreChartAddHint,
  type PreChartAddOffer,
  PreChartAddProgress,
  PreChartFileActions,
  PreChartReadingProgress,
} from "./PreChartFileActions";
import { PreChartLabsHint, type PreChartLabsOffer } from "./PreChartLabsHint";
import { TranscriptDrawer, TranscriptRow } from "./TranscriptDrawer";
import { useAskAssistant } from "./useAskAssistant";
import { filesToFill, isLabReport, usePreChartFiles } from "./usePreChartFiles";
import { usePreChartReads } from "./usePreChartReads";
import { VisitTitle, VisitTopBar } from "./VisitHeaderV3";
import { type PaneTab, useWorkspacePane } from "./workspacePane";

const noneIncluded = (): boolean => false;

// From this width the assistant is a column next to the note; below it, a
// drawer (tablet) or a bottom sheet (phone).
export const V3_ASSISTANT_COLUMN_MIN = 1280;
// From this width the right pane can dock (if the note keeps its room).
export const V3_PANE_MIN_VIEWPORT = 1024;
const PANE_WIDTH = 400;
const PANE_MIN_WIDTH = 340;
// 1024–1279px: a narrower pane.
const PANE_NARROW_WIDTH = 360;
const NOTE_MIN_WIDTH = 560;
// The Ask dock's transparent fade above its panel (pt-8).
const DOCK_FADE = 32;

// The segmented "Transcript | Assistant" switch at the top of the pane.
const PaneSwitch = ({
  tab,
  onChange,
}: {
  tab: PaneTab;
  onChange: (tab: PaneTab) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div
      aria-label={t("v3.pane.label")}
      className="inline-flex h-9 shrink-0 items-center rounded-full bg-v3-surface-2 p-[3px] shadow-v3-hairline"
      role="tablist"
    >
      {(["transcript", "assistant"] as const).map((value) => (
        <button
          key={value}
          aria-selected={tab === value}
          className={cn(
            "h-[30px] rounded-full px-3.5 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring",
            tab === value
              ? "bg-v3-panel text-v3-text shadow-[0_1px_2px_rgba(0,0,0,0.08),0_0_0_1px_var(--v3-border)]"
              : "text-v3-text-2 hover:text-v3-text",
          )}
          data-testid={`pane-tab-${value}`}
          role="tab"
          type="button"
          onClick={() => onChange(value)}
        >
          {value === "transcript"
            ? t("v3.pane.transcript")
            : t("v3.pane.assistant")}
        </button>
      ))}
    </div>
  );
};

interface Props {
  file: FileData | null;
  title: string;
  created: string | null;
  notices: React.ReactNode;
  // The documents (note, letters, summaries) with their review step.
  documents: React.ReactNode;
  create: React.ReactNode;
  copyToChart: CopyToChart;
  multiRecordingFiles: VisitRecording[];
  expandedItems: string[];
  toggleExpanded: (id: string) => void;
  onAddRecording?: () => void;
  documentName: string;
  target: NoteAssistantTarget;
  unavailable: AssistantUnavailableReason | null;
  sources?: SourcesProps;
  sourceIds: readonly number[];
  assistantOpen: boolean;
  onAssistantOpenChange: (open: boolean) => void;
  onApplied: () => void;
  onRename?: (title: string) => Promise<boolean>;
  onDelete?: () => void;
  deleting?: boolean;
  // Transcript edit (VITE_TRANSCRIPT_EDIT_ENABLED): "Edit transcript" in the
  // drawer, then the matching note update proposed in the assistant.
  canEditTranscript?: boolean;
  onTranscriptSaved?: (change: { before: string; after: string }) => void;
  assistantRequest?: AssistantRequest | null;
  onAssistantRequestTaken?: () => void;
  // A pre-chart draft (no recording yet): Start visit and Save draft, the
  // assistant to fill it, no transcript or Create.
  preChart?: PreChartActions;
  // Guided first visit: the practice consulta's guided note.
  practiceTour?: { open: boolean; reviewed: boolean; onClose: () => void };
}

// The visit workspace in the v3.2 layout (VITE_APP_REDESIGN_ENABLED), as the
// iPad and iPhone apps: a top bar, the note in one readable column (≤720px)
// under its title, with Export, Create and Copy to chart on its meta line
// (in the top bar once that scrolls away), the transcript in a drawer and the chat-style Ask bar at the bottom. Sending opens the assistant (a
// column from 1280px, a drawer or sheet below) where the proposal is
// reviewed and applied.
export const VisitWorkspaceV3 = ({
  file,
  title,
  created,
  notices,
  documents,
  create,
  copyToChart,
  multiRecordingFiles,
  expandedItems,
  toggleExpanded,
  onAddRecording,
  documentName,
  target,
  unavailable,
  sources,
  sourceIds,
  assistantOpen,
  onAssistantOpenChange,
  onApplied,
  onRename,
  onDelete,
  deleting = false,
  canEditTranscript = false,
  onTranscriptSaved,
  assistantRequest,
  onAssistantRequestTaken,
  preChart,
  practiceTour,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const wide = useMediaQuery(`(min-width: ${V3_ASSISTANT_COLUMN_MIN}px)`);
  const desktop = useMediaQuery(`(min-width: ${V3_PANE_MIN_VIEWPORT}px)`);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  // Desktop: one right pane ("Transcript | Assistant") next to the note,
  // when the note keeps at least NOTE_MIN_WIDTH; otherwise overlays.
  const root = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = root.current;
    if (!element) return;
    const measure = (): void => setWidth(element.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const paneWidth = wide
    ? PANE_WIDTH
    : Math.max(
        PANE_MIN_WIDTH,
        Math.min(PANE_NARROW_WIDTH, width - NOTE_MIN_WIDTH),
      );
  const docked = desktop && width > 0 && width - paneWidth >= NOTE_MIN_WIDTH;
  const pane = useWorkspacePane();
  useEffect(() => {
    useWorkspacePane.getState().setDocked(docked);
    return () => useWorkspacePane.getState().setDocked(false);
  }, [docked]);
  const paneOpen = docked && pane.open;
  const assistantShown = docked
    ? paneOpen && pane.tab === "assistant"
    : assistantOpen;
  // The parent opens the assistant (a request, What's new): in the pane.
  useEffect(() => {
    if (docked && assistantOpen) {
      useWorkspacePane.getState().show("assistant");
      onAssistantOpenChange(false);
    }
  }, [docked, assistantOpen, onAssistantOpenChange]);
  const showAssistant = (open: boolean): void => {
    if (!docked) onAssistantOpenChange(open);
    else if (open) useWorkspacePane.getState().show("assistant");
    else useWorkspacePane.getState().hide();
  };
  const openTranscript = (): void => {
    if (docked) useWorkspacePane.getState().toggle("transcript");
    else setTranscriptOpen(true);
  };
  const [renaming, setRenaming] = useState(false);
  // The Ask dock's height: it grows with the file tray, the "Adding…"
  // status and the suggestions, and the note keeps that much room below its
  // last line so nothing stays hidden under it.
  const [dock, setDock] = useState<HTMLDivElement | null>(null);
  const [dockHeight, setDockHeight] = useState(0);
  useEffect(() => {
    if (!dock) {
      setDockHeight(0);
      return;
    }
    const measure = (): void => setDockHeight(dock.offsetHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(dock);
    return () => observer.disconnect();
  }, [dock]);
  // The meta line has scrolled away: its actions move to the top bar.
  const [actionsInTopBar, setActionsInTopBar] = useState(false);
  // A pre-chart draft: the banner's Start visit has scrolled away, so the
  // top bar shows Start visit instead.
  const [startInTopBar, setStartInTopBar] = useState(false);
  const noteActions = (
    <NoteActions
      copyToChart={copyToChart}
      create={preChart ? null : create}
      preChart={!!preChart}
      inTopBar={actionsInTopBar}
    />
  );
  const ask = useAskAssistant({
    target,
    sourceIds,
    onApplied,
    preChartDraft: !!preChart,
  });
  const askRef = useRef(ask);
  askRef.current = ask;
  const sendRequest = useRef(ask.sendRequest);
  sendRequest.current = ask.sendRequest;
  const openAssistant = useRef(showAssistant);
  openAssistant.current = showAssistant;
  const taken = useRef(onAssistantRequestTaken);
  taken.current = onAssistantRequestTaken;
  // A prepared request (Update note after a transcript correction) is
  // proposed at once in the assistant.
  const requestKey = assistantRequest?.key;
  const request = useRef(assistantRequest);
  request.current = assistantRequest;
  useEffect(() => {
    const pending = request.current;
    if (requestKey === undefined || !pending) return;
    taken.current?.();
    sendRequest.current(pending);
    openAssistant.current(true);
  }, [requestKey]);
  // Names of the files picked here (both Ask bars share them), for the
  // hint, the "Spreading <file>" status and the toast.
  // Kept across reloads (useSourceNamesStore, set by AssistantSources).
  const fileNames = useSourceNames();
  // A pre-chart draft: an attached file waits until the clinician picks how
  // to add it (the hint above the Ask bar, or the file's preview).
  const preChartFiles = usePreChartFiles({
    enabled: !!preChart && !!sources,
    sources: sources?.sources,
    loading: !!sources?.loading,
    isIncluded: sources?.isIncluded ?? noneIncluded,
    names: fileNames,
    ask,
  });
  // Summarize and insert: the note's headings, the history heading first
  // choice.
  const noteText = preChart ? file?.result : null;
  const headings = useMemo(() => preChartHeadings(noteText), [noteText]);
  const assistantBusy =
    !!ask.auto ||
    ask.proposing ||
    ask.applying ||
    !!ask.result ||
    ask.undoing ||
    ask.queue.length > 0;
  // A pre-chart draft's template changed (PreChartTemplateChip): the
  // rewrite under the new headings is proposed for review.
  const rewrite = usePreChartStore((state) =>
    preChart && file ? state.templateRewrite : null,
  );
  const rewriteKey =
    rewrite && rewrite.visitPublicId === file?.public_id ? rewrite.key : null;
  useEffect(() => {
    if (rewriteKey === null) return;
    const request = usePreChartStore.getState().takeTemplateRewrite(rewriteKey);
    if (!request) return;
    // It only restructures what the draft has ("Results already in the
    // pre-chart stay"): no attached file is read again and the lab choice
    // is not applied.
    if (
      askRef.current.send(request.instruction, {
        sourceIds: [],
        preChartLabs: false,
      })
    )
      openAssistant.current(true);
  }, [rewriteKey]);
  // A pre-charted visit has no recording of its own (Start visit added it).
  const mainRecording = hasMainRecording(file);
  const recordings =
    (file && mainRecording ? 1 : 0) + multiRecordingFiles.length;
  const meta = [
    created,
    file && documentTemplateName(file),
    formatVisitDuration(file?.duration ?? null),
  ]
    .filter(Boolean)
    .join(" · ");
  // A pre-chart draft: the Ask bar invites dictation and uploads, and offers
  // to fill the pre-chart from the attached files.
  const askPlaceholder = preChart
    ? t("preChart.ask.placeholder")
    : t("v3.ask.placeholder");
  // An attached file waiting to be added: a short hint offers both ways
  // and says custom instructions can be typed in the Ask bar. It goes away
  // while something is typed or written, and once dismissed.
  // A lab report is offered as its values (which ones, and where), the
  // other files as Spread into sections or Summarize and insert.
  const waiting = preChart ? preChartFiles.offered : [];
  // Scans, photos and screenshots are read first (usePreChartReads): a lab
  // report among them gets the lab choice too.
  const reading = usePreChartReads(preChart ? file?.public_id : null, waiting);
  const readingFiles = waiting.filter((source) => reading.has(source.id));
  const ready = waiting.filter((source) => !reading.has(source.id));
  const labFiles = ready.filter(isLabReport);
  const offered = ready.filter((source) => !isLabReport(source));
  const hintsShown = !assistantBusy && !ask.draft.trim();
  const addHint = offered.length > 0 && hintsShown;
  const labTests = labFiles.flatMap((source) => reportLabTests(source.text));
  const account = useAuthStore((state) => state.userData?.public_id);
  const labsSetting = readAccountLabs(account);
  const labsOffer: PreChartLabsOffer | null =
    labFiles.length > 0 && hintsShown
      ? {
          count: labFiles.length,
          label: preChartFiles.label(labFiles),
          tests: labTests,
          headings: headings.headings,
          defaultHeading: labsDefaultHeading(headings.headings),
          initialChoice:
            labsSetting.mode === "tests"
              ? "custom"
              : labsSetting.mode === "abnormal"
                ? "abnormal"
                : "all",
          initialPicks: settingPicks(labsSetting, labTests),
          onAdd: (labs, heading) => preChartFiles.labs(labFiles, labs, heading),
          onDismiss: () => preChartFiles.dismiss(labFiles),
        }
      : null;
  const addOffer: PreChartAddOffer | null = addHint
    ? {
        count: offered.length,
        label: preChartFiles.label(offered),
        headings: headings.headings,
        defaultHeading: headings.history,
        onSpread: () => preChartFiles.spread(offered),
        onSummarize: (heading) => preChartFiles.summarize(offered, heading),
        onDismiss: () => preChartFiles.dismiss(offered),
      }
    : null;
  const fillIds = filesToFill(sourceIds, ask.undoneSources);
  const askSuggestions: AskSuggestion[] =
    // Not while the hint offers the same (Spread into sections).
    preChart &&
    fillIds.length > 0 &&
    !addHint &&
    !labsOffer &&
    !readingFiles.length
      ? [
          {
            id: "pre-chart-from-files",
            label: t("preChart.ask.fromFiles"),
            icon: "sources",
            onSelect: () => {
              // Files whose add was undone are left out (they stay marked
              // "Undone" in the tray until added again or removed).
              if (
                ask.send(t("preChart.ask.fromFilesInstruction"), {
                  sourceIds: fillIds,
                  preChartLabs: true,
                  // As Spread into sections.
                  preChartAdd: { mode: "spread" },
                })
              )
                showAssistant(true);
            },
          },
        ]
      : [];
  const switcher = (
    <PaneSwitch
      tab={pane.tab}
      onChange={(tab) => useWorkspacePane.getState().show(tab)}
    />
  );

  const conversation = (
    <AssistantConversation
      addOffer={addOffer}
      ask={ask}
      labsOffer={labsOffer}
      readingLabel={
        readingFiles.length > 0 && hintsShown
          ? preChartFiles.label(readingFiles)
          : null
      }
      documentName={documentName}
      preChart={!!preChart}
      sourceCount={sourceIds.length}
      unavailable={unavailable}
      withSources={!!sources}
    />
  );
  const withSources = (children: React.ReactNode, className?: string) =>
    sources ? (
      <AssistantSources
        className={className}
        {...sources}
        undoneIds={preChart ? ask.undoneSources : undefined}
        fileActions={
          preChart
            ? (source, close) =>
                sources?.isIncluded(source.id) ||
                ask.undoneSources.has(source.id) ? (
                  <PreChartFileActions
                    // Their own row, above Remove.
                    className="w-full"
                    defaultHeading={headings.history}
                    disabled={assistantBusy}
                    headings={headings.headings}
                    look="button"
                    onSpread={() => {
                      if (preChartFiles.spread([source])) close();
                    }}
                    onSummarize={(heading) => {
                      if (preChartFiles.summarize([source], heading)) close();
                    }}
                  />
                ) : null
            : undefined
        }
      >
        {children}
      </AssistantSources>
    ) : (
      children
    );

  return (
    <div
      ref={root}
      className="absolute inset-0 flex font-v3 text-v3-text"
      data-assistant-placement={docked ? "panel" : "overlay"}
      data-testid="visit-workspace"
    >
      <div className="relative flex min-w-0 flex-1 flex-col">
        <VisitTopBar
          primaryAction={
            // Once: in the top bar only while the banner's Start visit has
            // scrolled away, with the same fade as the note actions.
            preChart &&
            startInTopBar && (
              <StartVisitButton
                className="mr-1 animate-in fade-in slide-in-from-top-1 duration-150 motion-reduce:animate-none"
                disabled={preChart.startDisabled}
                size="compact"
                testId="top-bar-start-visit"
                onClick={preChart.onStartVisit}
              />
            )
          }
          noteActions={actionsInTopBar ? noteActions : undefined}
          deleting={deleting}
          disabled={!file}
          title={title}
          visitPublicId={file?.public_id ?? null}
          onDelete={() => onDelete?.()}
          onOpenTranscript={openTranscript}
          onRename={() => setRenaming(true)}
        />
        <div className="min-h-0 flex-1 overflow-y-auto" id={NOTE_SCROLLER_ID}>
          <div
            className="mx-auto w-full max-w-[720px] px-5 pb-44 pt-1 sm:px-8 lg:pt-3"
            style={
              dockHeight > 0
                ? { paddingBottom: Math.max(176, dockHeight + 16) }
                : undefined
            }
          >
            <NoteMetaBar
              actions={actionsInTopBar ? null : noteActions}
              meta={meta}
              onScrolledAway={setActionsInTopBar}
            />
            <VisitTitle
              disabled={!file || !onRename}
              renaming={renaming}
              title={title}
              onRename={(next) => onRename?.(next) ?? Promise.resolve(false)}
              onRenamingChange={setRenaming}
            />
            {file && !preChart && (
              <TranscriptRow
                file={file}
                recordings={recordings}
                transcription={
                  mainRecording
                    ? undefined
                    : (multiRecordingFiles[0]?.transcription ?? null)
                }
                onOpen={openTranscript}
              />
            )}
            {(notices || preChart) && (
              <div className="mb-4 space-y-3 empty:hidden">
                {preChart && (
                  <PreChartBanner
                    {...preChart}
                    onStartScrolledAway={setStartInTopBar}
                    // The dock's panel, not its 32px fade (pt-8).
                    coveredBelow={Math.max(0, dockHeight - DOCK_FADE)}
                    templateChip={
                      file ? <PreChartTemplateChip file={file} /> : undefined
                    }
                  />
                )}
                {notices}
              </div>
            )}
            {documents}
          </div>
        </div>
        {!(docked && assistantShown) && file && (
          // Same column as the note (720px, 20/32px gutters), 20px above the
          // card's bottom edge. A 32px fade, then the panel: the status, the
          // suggestions and the file tray never sit on the note's text.
          <div
            ref={setDock}
            className="pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-[linear-gradient(to_bottom,transparent,var(--v3-panel)_2rem)] pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-8"
            data-toast-anchor
          >
            <div
              className="pointer-events-auto mx-auto w-full max-w-[720px] px-5 sm:px-8"
              data-testid="note-ask-dock"
            >
              {ask.auto && (ask.proposing || ask.applying) && (
                <PreChartAddProgress
                  className="mb-2 shadow-v3-hairline"
                  kind={ask.auto.kind}
                  label={ask.auto.label}
                  saving={ask.applying}
                />
              )}
              {readingFiles.length > 0 && hintsShown && (
                <PreChartReadingProgress
                  className="mb-2 shadow-v3-hairline"
                  label={preChartFiles.label(readingFiles)}
                />
              )}
              {labsOffer && (
                <PreChartLabsHint
                  key={labFiles.map((source) => source.id).join()}
                  className="mb-2"
                  offer={labsOffer}
                />
              )}
              {addOffer && (
                <PreChartAddHint className="mb-2" offer={addOffer} />
              )}
              {ask.result && !assistantShown && !ask.auto && (
                <div className="mb-2 flex justify-center">
                  <V3Chip
                    data-testid="proposal-ready"
                    variant="suggestion"
                    onClick={() => showAssistant(true)}
                  >
                    <Sparkles aria-hidden />
                    {t("v3.ask.proposalReady")}
                  </V3Chip>
                </div>
              )}
              {withSources(
                <AskBar
                  attach={
                    sources ? <AttachSourceButton camera={false} /> : undefined
                  }
                  // A draft: the attached files show where they were added.
                  attachments={
                    sources && preChart ? <SourceChips thumbnails /> : undefined
                  }
                  className="max-w-none"
                  disabled={!!unavailable || ask.undoing}
                  focusShortcut={!assistantShown}
                  placeholder={askPlaceholder}
                  proposing={ask.proposing}
                  queueWhileBusy
                  suggestions={askSuggestions}
                  testId="note-ask"
                  value={ask.draft}
                  onChange={ask.setDraft}
                  onSend={() => {
                    if (ask.send()) showAssistant(true);
                  }}
                  onStop={ask.stop}
                />,
              )}
            </div>
          </div>
        )}
      </div>

      {paneOpen && (
        <aside
          aria-label={
            pane.tab === "assistant"
              ? t("v3.ask.title")
              : t("visit.workspace.transcript")
          }
          className="flex shrink-0 flex-col border-l border-v3-border bg-v3-panel"
          data-pane-tab={pane.tab}
          data-testid={
            pane.tab === "assistant" ? "assistant-panel" : "side-pane"
          }
          style={{ width: paneWidth }}
        >
          {pane.tab === "assistant" ? (
            <>
              <div className="flex h-[52px] shrink-0 items-center gap-1 pl-4 pr-2">
                {switcher}
                <V3IconButton
                  className="ml-auto"
                  data-testid="assistant-close"
                  label={t("v3.pane.hide")}
                  onClick={() => useWorkspacePane.getState().hide()}
                >
                  <PanelRightClose />
                </V3IconButton>
              </div>
              {withSources(conversation, "flex min-h-0 flex-1 flex-col")}
            </>
          ) : (
            <TranscriptDrawer
              canEdit={canEditTranscript}
              expandedItems={expandedItems}
              file={file}
              multiRecordingFiles={multiRecordingFiles}
              open
              pane={{
                switcher,
                onCollapse: () => useWorkspacePane.getState().hide(),
              }}
              toggleExpanded={toggleExpanded}
              onAddRecording={onAddRecording}
              onOpenChange={(open) => {
                if (!open) useWorkspacePane.getState().hide();
              }}
              onTranscriptSaved={(change) => {
                onTranscriptSaved?.(change);
                useWorkspacePane.getState().hide();
              }}
            />
          )}
        </aside>
      )}
      {practiceTour && (
        <PracticeTour
          // Desktop: the assistant is a column beside the note, so the tour
          // stays and goes on to Copy to chart. Narrower screens: the
          // assistant covers the note, so the tour waits at its stop.
          asked={ask.proposing || !!ask.result}
          open={practiceTour.open}
          paused={assistantShown && !docked}
          reviewed={practiceTour.reviewed}
          onClose={practiceTour.onClose}
          onPrefillAsk={ask.setDraft}
        />
      )}
      {!docked && (
        <V3Drawer
          className="max-sm:h-[88dvh] sm:w-[460px]"
          open={assistantOpen}
          testId="assistant-overlay"
          title={t("v3.ask.title")}
          onOpenChange={onAssistantOpenChange}
        >
          {withSources(conversation, "h-full flex flex-col")}
        </V3Drawer>
      )}
      {!docked && (
        <TranscriptDrawer
          canEdit={canEditTranscript}
          expandedItems={expandedItems}
          file={file}
          multiRecordingFiles={multiRecordingFiles}
          open={transcriptOpen}
          toggleExpanded={toggleExpanded}
          onAddRecording={onAddRecording}
          onOpenChange={setTranscriptOpen}
          onTranscriptSaved={onTranscriptSaved}
        />
      )}
    </div>
  );
};
